import { Fragment, type ReactNode, useLayoutEffect, useRef } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useQuery } from "@tanstack/react-query";
import { Button, NonIdealState, Spinner } from "@blueprintjs/core";
import { actAsUserIdentityAtom } from "./uiState/actAsUserAtom";
import { currentUserQueryOptions } from "./useCurrentUserData";
import { getUserStoragePrefix, userStorageScopeAtom } from "./userStorage";
import overlayElementsAtom from "./uiState/overlayElementsAtom";

export function UserStorageBoundary({
  tenantId,
  children,
}: {
  tenantId: string;
  children: ReactNode;
}) {
  const actAsUserId = useAtomValue(actAsUserIdentityAtom);
  const { data, error, refetch } = useQuery(
    currentUserQueryOptions(actAsUserId),
  );
  const [scope, setScope] = useAtom(userStorageScopeAtom);
  const hasLoadedUser = useRef(false);
  const setOverlays = useSetAtom(overlayElementsAtom);
  const nextScope = data?.currentUser
    ? getUserStoragePrefix(tenantId, data.currentUser.id)
    : null;

  useLayoutEffect(() => {
    if (nextScope) hasLoadedUser.current = true;
    setScope(nextScope);
    return () => {
      setScope(null);
      // Overlays live outside the shell; discard old-user forms during a switch too.
      setOverlays([]);
    };
  }, [nextScope, setScope, setOverlays]);

  if (error) {
    return (
      <NonIdealState
        title="Unable to load user settings"
        action={<Button text="Try again" onClick={() => void refetch()} />}
      />
    );
  }
  // Mount consumers only after the effective identity is known, including on Act As changes.
  if (!nextScope || scope !== nextScope)
    return hasLoadedUser.current ? null : <Spinner />;
  return <Fragment key={scope}>{children}</Fragment>;
}
